/**
 * S1 « Nouvel achat » — the pure model of the screen (refonte WP-22; design/ux/02_achats.md §2,
 * §4.5-§4.9). No I/O, no React: the entry band, the lines table, the totals band and the save
 * payload all read these functions, and every figure comes from the shared pricing engine
 * (`@pharmakon/shared/pricing`, the module the server stores with — ux/02 §2.7).
 *
 * Shapes
 *   saisie (one line, in the entry band and in the table) — typed values as canonical decimal
 *   strings (« 1250.50 ») or null, never binary floats:
 *     { cle, id, produit, quantite, ug, ug_pct, maitre_ug, numero_lot, date_peremption,
 *       prix_achat_ht, ristourne, taux_tva, prix_vente_ppa, shp, quantite_recue, recue_liee,
 *       mode_qte_recue, recue_modifiable, origine, a_verifier, verrouillee, ef_detail_id,
 *       prix_saisi, ppa_saisi, suggestion, destination }
 *     Quantities are what the operator TYPES in the facture's QTE reçue mode (refonte WP-63, D-25,
 *     A-74): « Qté » = the paid units (« Qté payée + UG ») or the paid units + UG (« UG incluses »);
 *     « Qté reçue » = the units that enter the stock (derived = Qté + UG, resp. Qté, while
 *     `recue_liee`; typed only when the owner flag `recue_modifiable` allows it, A-75 / A-100). The
 *     server receives the ONE stored meaning (quantite = paid units, ug, quantite_recue = paid units
 *     received) through `quantitesLigne` (shared `achat-quantites.js`).
 *     Refonte WP-65 (D-27, A-105) — `suggestion` is the last answer of `GET /achats/prix-suggeres` for
 *     the entry's product ({ produit_id, fournisseur_id, actif, mois, prix_achat_ht, prix_vente_ppa }
 *     or null); `prix_saisi` / `ppa_saisi` say the operator typed PU HT / PPA: a suggestion fills a
 *     field only while it is empty or still holds the previous suggestion, never a typed value.
 *     Refonte WP-69 (D-27, A-13; défaut A-120) — on a non-médicament line the « Prix de vente » (the
 *     stored `prix_vente_ppa`) and the PU HT are linked by `marge_pct` (« Marge % », 2 decimals):
 *     `marge_contexte` = { applicable, defaut, libelle } (the line's type, stamped by the S1 / S3
 *     reducer — `avecContexteMarge`), `ordre_prix` = the price fields the operator typed, most recent
 *     first, `prix_calcule` = the field the shared engine computed from the two others (`lierPrix`).
 *   entete (B1) —
 *     { fournisseur_id, fournisseur, numero_facture_fournisseur, date_facture, date_livraison,
 *       magasin_id, type_achat, notes, maitre_remise, remise_globale_montant, remise_globale_pct,
 *       declares: { montant_ht_declare … montant_facture_reelle }, mode_qte_recue }
 *     `mode_qte_recue` is the facture's snapshot (the settings' mode until the draft exists, D-25);
 *     it is never sent (the server sets it at creation, WP-62).
 */
import {
  ECHELLE_POURCENTAGE,
  ERREURS_QUANTITES_ACHAT,
  ETAT_ECART_TTC,
  MAITRE_REMISE,
  MAITRE_UG,
  MODE_QTE_RECUE,
  TOTAUX_DECLARES,
  calculerLigne,
  calculerTotaux,
  margePct,
  margeUnitaire,
  modeQteRecue,
  pctDepuisUgSaisie,
  prixAchatDepuisMarge,
  prixVenteDepuisMarge,
  quantiteRecueAttendue,
  quantitesSaisies,
  quantitesStockees,
  ugDepuisPctSaisie,
  ugIncluses,
  versCentiemesPct,
  versCentimes,
  versTexteDecimal,
  CANAL_VENTE,
  BUCKET_LOT,
} from "@pharmakon/shared/pricing";
import { SHP_PAR_CLASSIFICATION } from "@/config/shp.config.js";
import { margeAdmise } from "@pharmakon/shared/constants/produit-type-champs.js";
import {
  CHAMPS_PRIX_LIES,
  CHAMPS_SAISIE,
  MARGE_SAISIE,
  LIMITES_SAISIE_ACHAT as L,
  PEREMPTION_MASQUEE_POUR,
  PEREMPTION_MODE,
  PEREMPTION_OPTIONNELLE_POUR,
  SOURCE_PPA_SUGGERE,
  SOURCE_PRIX_SUGGERE,
  TAUX_TVA_ACHAT,
  TYPE_ACHAT_DEFAUT,
  TYPE_ACHAT_MIXTE,
} from "@/config/achat-saisie.config.js";
import { M_S1 } from "@/config/achat.messages.js";
import { PEREMPTION_SENTINELLE, estPeremptionSentinelle } from "@/lib/peremption.js";
// Refonte WP-66 (M-29; D-29) — the line's own magasin / emplacement (lib/achat-destination.js).
import { destinationDepuisServeur, destinationVersServeur } from "@/lib/achat-destination.js";
import { todayIso } from "@/lib/date-input.js";
// Refonte WP-68 (A-117) — the type KEY a line / the header compares (owner types: « Lait » ≠ Parapharmacie).
import { cleTypeAchat, cleTypeProduit, libelleCle, typeParCle } from "@/lib/types-produit.js";
import { formatDecimal, toDecimalString } from "@/lib/number-format.js";
import { fmtDate, fmtMoney } from "@/config/locale.config.js";

const vide = (v) => v === null || v === undefined || v === "";

/** Decimal string of a number / Prisma Decimal / string, or null. */
export function dec(v) {
  return vide(v) ? null : toDecimalString(v);
}

/** Integer string of a typed quantity, or null. */
function entier(v) {
  const d = dec(v);
  if (d == null) return null;
  return /^\d+$/.test(d) ? d : d.replace(/\.0+$/, "");
}

/** Typed quantity as a JS integer (0 when empty / unreadable). */
export function qte(v) {
  const n = Number(entier(v));
  return Number.isSafeInteger(n) ? n : 0;
}

let compteur = 0;
/** A client key for a line (stable through edits; the server id travels separately). */
export function nouvelleCle() {
  compteur += 1;
  return `l${Date.now().toString(36)}${compteur.toString(36)}`;
}

// ─── Produit ────────────────────────────────────────────────────────────────

/**
 * The product fields S1 uses, from a search row (`GET /produits`, `/produits/search`), a created
 * product or a facture line's `detail.produit` (ux/02 §4.1, AUD-ACHNEW-04).
 */
export function normaliserProduit(p) {
  if (!p || !p.id) return null;
  const forme = typeof p.forme === "string" ? p.forme : p.forme?.libelle ?? null;
  const dci = typeof p.dci === "string" ? p.dci : p.dci?.nom ?? null;
  const laboratoire =
    typeof p.laboratoire === "string" ? p.laboratoire : p.laboratoire?.nom ?? p.laboratoire_nom ?? null;
  return {
    id: p.id,
    nom_commercial: p.nom_commercial ?? "",
    dosage: p.dosage ?? null,
    forme,
    conditionnement: p.conditionnement ?? null,
    dci,
    laboratoire,
    type_produit: p.type_produit ?? null,
    // WP-68 — the owner type (null = the predefined type of type_produit) and its resume when the row has it
    type_officine_id: p.type_officine_id ?? p.type_officine?.id ?? null,
    type_officine: p.type_officine ?? null,
    is_psychotrope: Boolean(p.is_psychotrope),
    is_remboursable: Boolean(p.is_remboursable),
    is_local: Boolean(p.is_local),
    tarif_ref: dec(p.tarif_ref),
    ppa: dec(p.ppa),
    taux_tva: dec(p.taux_tva),
    shp: dec(p.shp),
    liste_classification: p.liste_classification ?? null,
    stock: p.qte_totale ?? p.stock ?? null,
    num_enr_cnas: p.num_enr_cnas ?? null,
  };
}

const plie = (s) => String(s ?? "").toLowerCase().replace(/\s+/g, "");

/**
 * « DOLIPRANE 1000 mg · Comprimé · B/8 » — the dosage is not repeated when the commercial name
 * already carries it (AUD-ACHNEW-18 « DOLIPRANE E2E 1000mg 1000mg »).
 */
export function libelleProduit(p, { forme = true, conditionnement = true } = {}) {
  if (!p) return "";
  const nom = p.nom_commercial ?? "";
  const dosage = p.dosage && !plie(nom).includes(plie(p.dosage)) ? ` ${p.dosage}` : "";
  return [`${nom}${dosage}`, forme && p.forme, conditionnement && p.conditionnement].filter(Boolean).join(" · ");
}

/** SHP of a product from its liste (shp.config.js), else its stored SHP (NouvelAchat.jsx:2118-2126). */
export function shpDuProduit(p) {
  if (p?.liste_classification != null && SHP_PAR_CLASSIFICATION[p.liste_classification] != null) {
    return dec(SHP_PAR_CLASSIFICATION[p.liste_classification]);
  }
  return dec(p?.shp) ?? "0";
}

// ─── Saisie (entry band) ────────────────────────────────────────────────────

/**
 * An empty entry: Qté 1 (as today, NouvelAchat.jsx:404), Qté reçue derived. `regle` = the facture's
 * QTE reçue rule `{ mode, modifiable }` (absent = « Qté payée + UG », not editable — the CONFIG
 * defaults; the S1 / S3 reducer stamps the facture's own rule on every entry, `avecRegleRecue`).
 */
export function nouvelleSaisie(regle = {}) {
  return {
    cle: nouvelleCle(),
    id: null,
    produit: null,
    quantite: "1",
    ug: "0",
    ug_pct: null,
    maitre_ug: MAITRE_UG.UG,
    numero_lot: "",
    date_peremption: "",
    prix_achat_ht: null,
    ristourne: null,
    taux_tva: "0",
    prix_vente_ppa: null,
    shp: "0",
    quantite_recue: "1",
    recue_liee: true,
    // Refonte WP-63 (D-25, A-100) — the rule the quantities are typed in.
    mode_qte_recue: modeQteRecue(regle.mode),
    recue_modifiable: Boolean(regle.modifiable),
    origine: "MANUELLE",
    a_verifier: false,
    verrouillee: false,
    ef_detail_id: null,
    texte_recherche: null,
    // Refonte WP-33 (ux/03 §3.6, DB-18) — the bon de commande lines this facture line invoices
    // ([{ ligne_bon_commande_id, bon_commande_id, numero, reste? }]); null = none.
    bons_commande: null,
    // Refonte WP-65 (D-27, A-105) — PU HT / PPA typed by the operator (a suggestion never replaces
    // them) and the last price suggestion read for the entry's product.
    prix_saisi: false,
    ppa_saisi: false,
    suggestion: null,
    // Refonte WP-69 (D-27, A-120) — the margin of a non-médicament line (2-decimal string), the price
    // fields typed (most recent first), the field computed from the two others, the type's context.
    marge_pct: null,
    ordre_prix: [],
    prix_calcule: null,
    marge_contexte: null,
    // Refonte WP-66 (D-29) — null = the facture's magasin; else the line's own
    // { magasin_id, magasin_nom, emplacement_id, emplacement_code } (lib/achat-destination.js).
    destination: null,
  };
}

/** Two money values (decimal strings / numbers) are the same amount to the centime; empty ≠ 0. */
function memeMontant(a, b) {
  if (vide(a) || vide(b)) return vide(a) && vide(b);
  try {
    return versCentimes(dec(a)) === versCentimes(dec(b));
  } catch {
    return String(a) === String(b);
  }
}

/** The field still holds what the last suggestion put there (or nothing): a suggestion may replace it. */
const tientSuggestion = (valeur, suggere) => vide(valeur) || (!vide(suggere) && memeMontant(valeur, suggere));

/**
 * Picking a product fills TVA, PPA, SHP and T.R (ux/02 §4.7 « Picking a product fills … »); what the
 * operator typed from the paper (quantities, PU HT, lot, péremption) is kept. Refonte WP-65 (D-27): a
 * typed PPA is kept too (`ppa_saisi`); a PU HT / PPA that only held the previous product's
 * suggestion is cleared — the new product's suggestion follows (`appliquerSuggestionPrix`).
 */
export function appliquerProduit(s, produit) {
  const p = normaliserProduit(produit);
  if (!p) return { ...s, produit: null };
  const memeProduit = s.produit?.id === p.id;
  const ancienne = memeProduit ? null : s.suggestion;
  // WP-69 — a price the margin computed for the previous product is not the operator's either
  const calcule = memeProduit ? null : s.prix_calcule ?? null;
  const puSuggere =
    !s.prix_saisi && !vide(s.prix_achat_ht) && (calcule === CHAMP_PU || memeMontant(s.prix_achat_ht, ancienne?.prix_achat_ht?.valeur));
  // the previous product's PPA (its fiche's, or the one its suggestion gave) is not the operator's
  const ancienPpa = memeProduit ? null : ancienne?.prix_vente_ppa?.valeur ?? s.produit?.ppa ?? null;
  const ppaSuggere = !vide(s.prix_vente_ppa) && (calcule === CHAMP_PV || memeMontant(s.prix_vente_ppa, ancienPpa));
  let ppa = s.prix_vente_ppa;
  if (!s.ppa_saisi) ppa = p.ppa ?? (ppaSuggere ? null : s.prix_vente_ppa);
  return lierPrix({
    ...s,
    produit: p,
    taux_tva: p.taux_tva != null ? String(Number(p.taux_tva)) : s.taux_tva ?? "0",
    prix_vente_ppa: ppa,
    prix_achat_ht: puSuggere ? null : s.prix_achat_ht,
    shp: shpDuProduit(p),
    suggestion: memeProduit ? s.suggestion ?? null : null,
    prix_calcule: memeProduit ? s.prix_calcule ?? null : null,
  });
}

/**
 * PU HT typed (refonte WP-65): from now on a suggestion never replaces it. The value the field hands
 * back unchanged on Tab / Entrée (WP-61) changes nothing — an accepted suggestion stays a suggestion.
 */
export function changerPrixAchat(s, valeur) {
  if (memeMontant(valeur, s.prix_achat_ht)) return s;
  return lierPrix({ ...s, prix_achat_ht: valeur, prix_saisi: true, ordre_prix: ordreApresSaisie(s, CHAMP_PU) });
}

/**
 * PPA / Prix de vente typed (refonte WP-65): kept against the product's PPA and any suggestion;
 * unchanged = no-op. WP-69 — on a non-médicament line the margin links it to the PU HT (`lierPrix`).
 */
export function changerPpa(s, valeur) {
  if (memeMontant(valeur, s.prix_vente_ppa)) return s;
  return lierPrix({ ...s, prix_vente_ppa: valeur, ppa_saisi: true, ordre_prix: ordreApresSaisie(s, CHAMP_PV) });
}

/**
 * Refonte WP-69 (D-27, A-120) — Marge % typed on a non-médicament line: the price typed before it is
 * kept and the other one follows (`lierPrix`). The value the field hands back unchanged on Tab /
 * Entrée changes nothing (WP-61): a default or computed margin read through stays what it was.
 */
export function changerMarge(s, valeur) {
  if (!margeApplicable(s) || memePct(valeur, s.marge_pct)) return s;
  return lierPrix({ ...s, marge_pct: valeur, ordre_prix: ordreApresSaisie(s, CHAMP_MARGE) });
}

/**
 * The rows of `GET /achats/prix-suggeres` from the response BODY (`{ success, data: { fournisseur_id,
 * suggestion_dernier_prix, mois, data: [...] } }`) — the suggestion of `produitId` as the entry keeps
 * it, or null when the answer does not name it.
 */
export function suggestionDepuisReponse(body, produitId) {
  const d = body?.data && Array.isArray(body.data.data) ? body.data : body;
  const ligne = Array.isArray(d?.data) ? d.data.find((x) => x.produit_id === produitId) : null;
  if (!ligne) return null;
  return {
    produit_id: produitId,
    fournisseur_id: d.fournisseur_id ?? null,
    actif: d.suggestion_dernier_prix !== false,
    mois: d.mois ?? null,
    prix_achat_ht: d.suggestion_dernier_prix === false ? null : ligne.prix_achat_ht ?? null,
    prix_vente_ppa: ligne.prix_vente_ppa ?? null,
  };
}

/**
 * Refonte WP-65 (D-27, A-105) — the price suggestion of the entry's product answered: PU HT (when the
 * setting is on) and PPA fill their field only while it is empty or still holds the previous
 * suggestion — never a typed value (`prix_saisi`, `ppa_saisi`); a previous suggestion the new
 * answer no longer gives (another fournisseur, the setting off) is cleared. An answer for another
 * product than the entry's is dropped (the same object).
 */
export function appliquerSuggestionPrix(s, suggestion) {
  if (!s?.produit || !suggestion || suggestion.produit_id !== s.produit.id) return s;
  const avant = s.suggestion;
  const n = { ...s, suggestion };
  // WP-69 — a price the margin computed is no typed value either: the suggestion may take its place
  // (`lierPrix` then decides again which of the three fields follows the two others).
  if (!s.prix_saisi && (s.prix_calcule === CHAMP_PU || tientSuggestion(s.prix_achat_ht, avant?.prix_achat_ht?.valeur))) {
    const v = suggestion.prix_achat_ht?.valeur ?? null;
    n.prix_achat_ht = v != null ? dec(v) : vide(avant?.prix_achat_ht?.valeur) ? s.prix_achat_ht : null;
    if (v != null && s.prix_calcule === CHAMP_PU) n.prix_calcule = null;
  }
  if (!s.ppa_saisi && (s.prix_calcule === CHAMP_PV || tientSuggestion(s.prix_vente_ppa, avant?.prix_vente_ppa?.valeur ?? s.produit.ppa))) {
    const v = suggestion.prix_vente_ppa?.valeur ?? null;
    if (v != null) {
      n.prix_vente_ppa = dec(v);
      if (s.prix_calcule === CHAMP_PV) n.prix_calcule = null;
    }
  }
  return lierPrix(n);
}

// ─── Prix de vente ↔ marge (refonte WP-69; D-27, A-13, défaut A-120) ─────────

const [CHAMP_PU, CHAMP_MARGE, CHAMP_PV] = CHAMPS_PRIX_LIES;

/** No margin on the line (Médicament, « Mixte » before a product, a type not known yet). */
const SANS_MARGE = Object.freeze({ applicable: false, defaut: null, libelle: null });

/** The margin applies to the entry (its type takes one): « Marge % » shown, the PPA field reads « Prix de vente ». */
export const margeApplicable = (s) => Boolean(s?.marge_contexte?.applicable);

/**
 * The margin context of an entry: the type of its product, else (no product yet) the facture's Type
 * d'achat — « Mixte » has none. `applicable` when that type's base takes a margin (`margeAdmise`: not
 * a Médicament base, A-77); `defaut` = the type's margin in the officine's catalogue (Paramètres ›
 * Types de produits, WP-67) or null; `libelle` = the type's name for the note.
 * @param {object|null} produit  the entry's product (normaliserProduit shape)
 * @param {object} entete        the facture header (type_achat, type_achat_officine_id)
 * @param {object[]} types       the officine's catalogue (useCatalogueTypes)
 */
export function contexteMarge(produit, entete, types) {
  const typeAchat = entete?.type_achat && entete.type_achat !== TYPE_ACHAT_MIXTE ? entete.type_achat : null;
  const base = produit ? produit.type_produit ?? null : typeAchat;
  if (!base || !margeAdmise(base)) return SANS_MARGE;
  const cle = produit ? cleTypeProduit(produit) : cleTypeAchat(entete);
  const type = typeParCle(types).get(cle) ?? null;
  return {
    applicable: true,
    defaut: type && !vide(type.marge_pct) ? dec(type.marge_pct) : null,
    libelle: type?.libelle ?? libelleCle(cle, types, { resume: produit ? produit.type_officine : entete?.type_achat_officine }),
  };
}

const memeContexte = (a, b) =>
  Boolean(a?.applicable) === Boolean(b?.applicable) && (a?.defaut ?? null) === (b?.defaut ?? null) && (a?.libelle ?? null) === (b?.libelle ?? null);

/** The entry with its margin context (the same object when it already has it), the prices linked again. */
export function avecContexteMarge(s, ctx = SANS_MARGE) {
  if (!s || memeContexte(s.marge_contexte, ctx)) return s;
  return lierPrix({ ...s, marge_contexte: ctx });
}

/** The price fields typed once `champ` is typed: it first (empty while being typed), then the others that hold a value. */
function ordreApresSaisie(s, champ) {
  return [champ, ...(s.ordre_prix ?? []).filter((c) => c !== champ && CHAMPS_PRIX_LIES.includes(c) && !vide(s[c]))];
}

const memesListes = (a, b) => (a ?? []).length === (b ?? []).length && (a ?? []).every((x, i) => x === b[i]);

/**
 * Refonte WP-69 (D-27, A-13; défaut A-120) — PU HT, Marge % and Prix de vente of a non-médicament line,
 * linked by the shared engine (`prixVenteDepuisMarge`, `prixAchatDepuisMarge`, `margePct` at 2 decimals):
 * the two fields given last are kept, the third is computed (the A-01 « last typed is the master »
 * rule on three fields). Ranked: what the operator typed, most recent first; then the values the line
 * holds untyped — PU HT (suggestion, prefill, a saved line) before Prix de vente (the fiche's, the last
 * purchase's) before the type's default margin; a computed value is never a given. The PU HT is computed
 * only from a Prix de vente or a margin the operator typed (never from suggestions alone). When nothing
 * can be computed, a value computed before goes with its link and the margin shows the type's default.
 * A typed price stops being « typed » (WP-65 flags) once computed. Médicament: no margin, nothing linked.
 * Returns the same object when nothing changes.
 */
export function lierPrix(s) {
  if (!s) return s;
  const ordre = (Array.isArray(s.ordre_prix) ? s.ordre_prix : []).filter((c) => CHAMPS_PRIX_LIES.includes(c));
  if (!margeApplicable(s)) {
    if (vide(s.marge_pct) && !s.prix_calcule && !ordre.includes(CHAMP_MARGE)) return s;
    const n = { ...s, marge_pct: null, prix_calcule: null, ordre_prix: ordre.filter((c) => c !== CHAMP_MARGE) };
    if (s.prix_calcule === CHAMP_PU || s.prix_calcule === CHAMP_PV) n[s.prix_calcule] = null;
    return n;
  }
  const defaut = s.marge_contexte?.defaut ?? null;
  const donnee = (c) => {
    if (ordre.includes(c)) return s[c];
    if (c === s.prix_calcule) return null;
    return c === CHAMP_MARGE ? defaut : s[c];
  };
  const libres = [CHAMP_PU, CHAMP_PV, CHAMP_MARGE].filter((c) => !ordre.includes(c));
  const rang = [...ordre, ...libres.filter((c) => !vide(donnee(c))), ...libres.filter((c) => vide(donnee(c)))];
  const [a, b, cible] = rang;
  let calculable = !vide(donnee(a)) && !vide(donnee(b)) && (cible !== CHAMP_PU || ordre.includes(a) || ordre.includes(b));
  let valeur = null;
  if (calculable) {
    try {
      if (cible === CHAMP_PV) valeur = versTexteDecimal(prixVenteDepuisMarge(dec(donnee(CHAMP_PU)), dec(donnee(CHAMP_MARGE))), 2);
      else if (cible === CHAMP_PU) valeur = versTexteDecimal(prixAchatDepuisMarge(dec(donnee(CHAMP_PV)), dec(donnee(CHAMP_MARGE))), 2);
      else {
        const m = margePct(dec(donnee(CHAMP_PV)), dec(donnee(CHAMP_PU)), ECHELLE_POURCENTAGE);
        valeur = m == null ? null : versTexteDecimal(versCentiemesPct(m), ECHELLE_POURCENTAGE);
      }
    } catch {
      calculable = false; // an unreadable or out-of-bounds value: nothing is computed from it
    }
  }
  const n = { ...s, ordre_prix: ordre };
  if (calculable) {
    n[cible] = valeur;
    n.prix_calcule = cible;
    n.ordre_prix = ordre.filter((c) => c !== cible);
    if (cible === CHAMP_PU) n.prix_saisi = false;
    if (cible === CHAMP_PV) n.ppa_saisi = false;
  } else {
    if (s.prix_calcule && !ordre.includes(s.prix_calcule)) n[s.prix_calcule] = null;
    n.prix_calcule = null;
  }
  if (!n.ordre_prix.includes(CHAMP_MARGE) && n.prix_calcule !== CHAMP_MARGE) n.marge_pct = defaut;
  const meme =
    n.prix_achat_ht === s.prix_achat_ht &&
    n.prix_vente_ppa === s.prix_vente_ppa &&
    (n.marge_pct ?? null) === (s.marge_pct ?? null) &&
    (n.prix_calcule ?? null) === (s.prix_calcule ?? null) &&
    n.prix_saisi === s.prix_saisi &&
    n.ppa_saisi === s.ppa_saisi &&
    memesListes(n.ordre_prix, s.ordre_prix);
  return meme ? s : n;
}

/**
 * The Marge % note (M-S1-20c, the band's notes line): computed from the two prices, or the type's
 * default margin while it is the margin shown; null when the operator typed it (or no margin applies).
 */
export function indiceMarge(s) {
  if (!margeApplicable(s)) return null;
  if (s.prix_calcule === CHAMP_MARGE) return M_S1.MARGE_CALCULEE;
  const defaut = s.marge_contexte?.defaut ?? null;
  if (!(s.ordre_prix ?? []).includes(CHAMP_MARGE) && !vide(defaut) && s.marge_contexte?.libelle) return M_S1.MARGE_DU_TYPE(s.marge_contexte.libelle);
  return null;
}

/** The line confirmed into the table: its prices stay, the margin is read again from them when it returns to the band. */
export const sansLiensPrix = (s) => ({ ...s, ordre_prix: [], prix_calcule: null, marge_contexte: null, marge_pct: null });

const montantIndice = (valeur, champ) => (vide(valeur) || memeMontant(valeur, champ) ? null : fmtMoney(valeur));
const dateIndice = (iso) => (iso ? fmtDate(iso) : null);

/**
 * The PU HT note (M-S1-16b, the band's notes line): where the suggested price comes from — this fournisseur, another
 * one (named), or none in the window; the amount only when the field holds another value. Null
 * while nothing was read, or when the owner turned the suggestion off.
 */
export function indicePrixSuggere(s) {
  const sug = s?.suggestion;
  if (!s?.produit || !sug || sug.produit_id !== s.produit.id || !sug.actif || !sug.prix_achat_ht) return null;
  const p = sug.prix_achat_ht;
  const montant = montantIndice(p.valeur, s.prix_achat_ht);
  if (p.source === SOURCE_PRIX_SUGGERE.FOURNISSEUR) return M_S1.PU_SUGGERE_FOURNISSEUR(dateIndice(p.date), montant);
  if (p.source === SOURCE_PRIX_SUGGERE.DERNIER_CONNU) return M_S1.PU_SUGGERE_AUTRE(p.fournisseur?.nom ?? null, dateIndice(p.date), montant);
  return sug.mois ? M_S1.PU_SUGGERE_AUCUN(sug.mois) : null;
}

/**
 * The PPA note (M-S1-20b, the band's notes line): the PPA taken from the latest purchase (the product has none), or —
 * beside a typed PPA — the fiche's / the latest purchase's. Null when the field holds the fiche PPA.
 */
export function indicePpaSuggere(s) {
  const sug = s?.suggestion;
  if (!s?.produit || !sug || sug.produit_id !== s.produit.id || !sug.prix_vente_ppa?.valeur) return null;
  const p = sug.prix_vente_ppa;
  const montant = montantIndice(p.valeur, s.prix_vente_ppa);
  // WP-69 — a non-médicament line names its field « Prix de vente »
  const pv = margeApplicable(s);
  if (p.source === SOURCE_PPA_SUGGERE.DERNIER_ACHAT) return (pv ? M_S1.PV_SUGGERE_ACHAT : M_S1.PPA_SUGGERE_ACHAT)(dateIndice(p.date), montant);
  if (p.source === SOURCE_PPA_SUGGERE.PRODUIT && montant) return (pv ? M_S1.PV_SUGGERE_PRODUIT : M_S1.PPA_SUGGERE_PRODUIT)(montant);
  return null;
}

/** The QTE reçue mode an entry is typed in (D-25; absent = « Qté payée + UG »). */
export const modeDeSaisie = (s) => modeQteRecue(s?.mode_qte_recue);

/**
 * A-01 — UG of a UG % (floor) as a string, on the typed Qté in the entry's mode: UG per 100 PAID
 * units (D-04) in both modes — in « UG incluses » the typed Qté holds the UG (A-74).
 */
function ugDe(s, quantite, pct) {
  if (vide(pct)) return "0";
  return String(ugDepuisPctSaisie(modeDeSaisie(s), qte(quantite), versCentiemesPct(pct)));
}

/** A-01 — UG % back-computed from UG (2 decimals) as a string, or null while there is no paid unit. */
function pctDe(s, ug, quantite) {
  const c = pctDepuisUgSaisie(modeDeSaisie(s), qte(ug), qte(quantite));
  return c == null ? null : versTexteDecimal(c, 2);
}

/**
 * D-25 — the Qté reçue of an entry while it is derived: the units the facture's mode makes enter
 * the stock — Qté payée + UG (« Qté payée + UG »), the typed Qté (« UG incluses »). The one
 * definition of « the total » Entrée / Tab on UG copies into Qté reçue (WP-61 → WP-63).
 */
export function quantiteRecueDerivee(s) {
  return String(quantiteRecueAttendue(modeDeSaisie(s), qte(s.quantite), qte(s.ug)));
}

/** Qté reçue, while derived, is re-derived (the same object when it already is). */
function avecRecueDerivee(s) {
  if (!s.recue_liee) return s;
  const recue = quantiteRecueDerivee(s);
  return recue === s.quantite_recue ? s : { ...s, quantite_recue: recue };
}

/**
 * After a change of Qté, UG or UG %: Qté reçue follows while derived; a typed one stays only while
 * the owner flag lets it be typed (A-100) — otherwise the line enters the stock in full again.
 */
function apresChangementQuantites(n) {
  if (!n.recue_liee && n.recue_modifiable) return n;
  return avecRecueDerivee(n.recue_liee ? n : { ...n, recue_liee: true });
}

/**
 * The stored quantities of an entry (refonte WP-63): what the server receives and the engine
 * computes — paid units, UG, paid units received — with the A-74 / A-75 / A-99 refusals
 * (`ERREURS_QUANTITES_ACHAT`), the derived Qté reçue, the paid units short and the stock entry.
 */
export function quantitesLigne(s) {
  return quantitesStockees(modeDeSaisie(s), {
    quantite: s.quantite,
    ug: s.ug,
    maitre_ug: MAITRE_UG.UG, // the entry's UG already follows its % (changerQuantite / changerUgPct)
    quantite_recue: s.recue_liee ? null : s.quantite_recue,
  });
}

/**
 * The entry re-typed in the facture's QTE reçue rule `{ mode, modifiable }` (D-25, A-100): the same
 * stored quantities read in the new mode (a prefill from a bon de commande or an e-facture carries
 * paid units + UG; a draft created in another mode than the settings assumed); an entry that never
 * reached the server and whose typed Qté reçue the flag no longer allows is derived again. The
 * same object when nothing changes.
 */
export function avecRegleRecue(s, regle = {}) {
  if (!s) return s;
  const mode = modeQteRecue(regle.mode);
  const modifiable = Boolean(regle.modifiable);
  if (s.mode_qte_recue === mode && s.recue_modifiable === modifiable) return s;
  let n = { ...s, mode_qte_recue: mode, recue_modifiable: modifiable };
  const ancien = modeDeSaisie(s);
  if (ancien !== mode && /^\d+$/.test(String(s.quantite ?? "").trim())) {
    const vue = quantitesSaisies(mode, quantitesStockees(ancien, { quantite: s.quantite, ug: s.ug, quantite_recue: s.recue_liee ? null : s.quantite_recue }));
    n = { ...n, quantite: String(vue.quantite), ug: String(vue.ug), quantite_recue: String(vue.quantite_recue) };
  }
  if (!modifiable && !n.recue_liee && !n.id) n = { ...n, recue_liee: true };
  return avecRecueDerivee(n);
}

/** The same UG (an integer field: « » and « 0 » are both none). */
const memeUg = (a, b) => qte(a) === qte(b);

/** The same UG % to the hundredth (the A-01 2-decimal scale), « » and null being none. */
function memePct(a, b) {
  if (vide(a) || vide(b)) return vide(a) && vide(b);
  try {
    return versCentiemesPct(a) === versCentiemesPct(b);
  } catch {
    return String(a) === String(b);
  }
}

/**
 * Qté typed: Qté reçue follows while derived; UG or UG % follows the master (A-01) in the entry's
 * mode. The value handed back unchanged on Tab / Entrée changes nothing (a stored Qté reçue kept
 * by an unchanged line is never re-derived by a blur — WP-61, WP-63).
 */
export function changerQuantite(s, quantite) {
  if (vide(quantite) === vide(s.quantite) && qte(quantite) === qte(s.quantite)) return avecRecueDerivee(s);
  const n = { ...s, quantite };
  if (s.maitre_ug === MAITRE_UG.UG_PCT) n.ug = ugDe(s, quantite, s.ug_pct);
  else n.ug_pct = qte(s.ug) > 0 ? pctDe(s, s.ug, quantite) : null;
  return apresChangementQuantites(n);
}

/**
 * UG typed: UG is the master, UG % is back-computed for display (A-01). The value the field hands
 * back when the operator leaves it unchanged (blur on Tab / Entrée, WP-61) changes nothing: a UG
 * derived from a typed UG % never becomes the master; the derived Qté reçue is copied (D-25).
 */
export function changerUg(s, ug) {
  if (memeUg(ug, s.ug)) return avecRecueDerivee(s);
  return apresChangementQuantites({ ...s, ug, maitre_ug: MAITRE_UG.UG, ug_pct: qte(ug) > 0 ? pctDe(s, ug, s.quantite) : null });
}

/**
 * UG % typed: UG % is the master, UG = floor(Qté × % ÷ 100) (A-01). Leaving the field unchanged
 * changes nothing (WP-61, AUD-CP-01): the back-computed UG % of a typed UG (« 33,33 » for 1 on 3)
 * never becomes the master, so Tab through it never floors that UG to 0.
 */
export function changerUgPct(s, pct) {
  if (memePct(pct, s.ug_pct)) return avecRecueDerivee(s);
  return apresChangementQuantites({ ...s, ug_pct: pct, maitre_ug: MAITRE_UG.UG_PCT, ug: ugDe(s, s.quantite, pct) });
}

/**
 * Qté reçue typed: it stops being derived (ux/02 §4.7 #12) — only while the owner flag lets it be
 * typed (D-25, A-100); read-only otherwise. Leaving the field unchanged (Tab, or the Entrée that adds
 * the line) keeps it derived (WP-61): only a typed value unlinks it.
 */
export function changerRecue(s, recue) {
  if (!s.recue_modifiable) return s;
  if (vide(recue) === vide(s.quantite_recue) && qte(recue) === qte(s.quantite_recue)) return s;
  return { ...s, quantite_recue: recue, recue_liee: false };
}

/** The ⛓ toggle: Qté reçue is derived again. */
export function lierRecue(s) {
  return avecRecueDerivee({ ...s, recue_liee: true });
}

/** ux/02 §2.1 — the rounding hint of UG % (« 5 % de 30 = 1,5 → 1 UG (arrondi inférieur) »), or null. */
export function indiceArrondiUg(s) {
  if (s.maitre_ug !== MAITRE_UG.UG_PCT || vide(s.ug_pct)) return null;
  const q = qte(s.quantite);
  const pct = versCentiemesPct(s.ug_pct);
  if (q === 0 || pct === 0) return null;
  const p = formatDecimal(s.ug_pct, { decimals: 2 }).replace(/,00$/, "");
  const ug = ugDe(s, q, s.ug_pct);
  if (ugIncluses(modeDeSaisie(s))) {
    // A-74 / A-99 — the % of the paid units, the Qté holding the UG: Qté × % ÷ (100 + %).
    const denominateur = 10000 + pct;
    if ((q * pct) % denominateur === 0) return null;
    const x = formatDecimal(versTexteDecimal(Math.round((q * pct * 100) / denominateur), 2), { decimals: 2 }).replace(/,?0+$/, "");
    return M_S1.UG_PCT_ARRONDI_INCLUSES(p, q, x, ug);
  }
  const exactCentiemes = q * pct; // q × pct (hundredths) ÷ 100 = units × 100
  if (exactCentiemes % 10000 === 0) return null;
  const x = formatDecimal(versTexteDecimal(Math.round(exactCentiemes / 100), 2), { decimals: 2 }).replace(/,?0+$/, "");
  return M_S1.UG_PCT_ARRONDI(p, q, x, ug);
}

// ─── Péremption (A-03) ──────────────────────────────────────────────────────

/** A-03 — how the péremption field behaves for a line: by its product type, else the Type d'achat. */
export function modePeremption(typeProduit, typeAchat) {
  const t = typeProduit ?? (typeAchat && typeAchat !== TYPE_ACHAT_MIXTE ? typeAchat : null);
  if (t && PEREMPTION_MASQUEE_POUR.includes(t)) return PEREMPTION_MODE.MASQUEE;
  if (t && PEREMPTION_OPTIONNELLE_POUR.includes(t)) return PEREMPTION_MODE.OPTIONNELLE;
  return PEREMPTION_MODE.REQUISE;
}

/** The date the server receives: the typed one, else the « sans péremption » sentinel when allowed. */
export function peremptionEnvoyee(s, typeAchat) {
  if (!vide(s.date_peremption)) return s.date_peremption;
  return modePeremption(s.produit?.type_produit, typeAchat) === PEREMPTION_MODE.REQUISE ? null : PEREMPTION_SENTINELLE;
}

// ─── Enter chain (ux/02 §4.7) ───────────────────────────────────────────────

/** The fields of the band in Entrée order for this entry (UG % only while UG is 0; péremption when shown). */
export function chaineSaisie(s, typeAchat) {
  return CHAMPS_SAISIE.filter((c) => {
    if (c === "ug_pct") return qte(s.ug) === 0 || s.maitre_ug === MAITRE_UG.UG_PCT;
    if (c === CHAMP_MARGE) return margeApplicable(s); // WP-69 — a non-médicament line only
    if (c === "date_peremption") return modePeremption(s.produit?.type_produit, typeAchat) !== PEREMPTION_MODE.MASQUEE;
    return true;
  });
}

/** The next field after `champ` on Entrée, or null on the last one (Entrée = Ajouter / Mettre à jour). */
export function champSuivant(champ, s, typeAchat) {
  const chaine = chaineSaisie(s, typeAchat);
  const i = chaine.indexOf(champ);
  if (i < 0) {
    // A field skipped by the chain (UG % after a UG was typed): continue from its static position.
    const j = CHAMPS_SAISIE.indexOf(champ);
    return chaine.find((c) => CHAMPS_SAISIE.indexOf(c) > j) ?? null;
  }
  return chaine[i + 1] ?? null;
}

// ─── Validation of one line (M-S1-10 … M-S1-21, M-S1-30) ───────────────────

/**
 * @param {object} s the entry
 * @param {object} ctx
 * @param {string} ctx.typeAchat
 * @param {object[]} [ctx.lignes] the facture's lines (the D-14 psychotrope rule)
 * @param {string} [ctx.aujourdhui] ISO date (tests)
 * @returns {{ erreurs: Record<string,string>, avertissements: Record<string,string>, premier: string|null }}
 */
export function verifierSaisie(s, { typeAchat, lignes = [], aujourdhui = todayIso() } = {}) {
  const erreurs = {};
  const avertissements = {};
  const p = s.produit;
  if (!p) erreurs.produit = M_S1.PRODUIT_REQUIS;
  else if (psychotropeRefuse(p, lignes, s.cle)) erreurs.produit = M_S1.PSYCHOTROPE_MELANGE;

  const q = dec(s.quantite);
  if (q == null || !/^\d+$/.test(q) || Number(q) < 1 || Number(q) > L.QUANTITE_MAX) {
    erreurs.quantite = M_S1.QTE_INVALIDE(formatDecimal(L.QUANTITE_MAX, { decimals: 0 }));
  }
  const ug = dec(s.ug);
  if (ug != null && (!/^\d+$/.test(ug) || Number(ug) > L.QUANTITE_MAX)) {
    erreurs.ug = M_S1.UG_INVALIDE(formatDecimal(L.QUANTITE_MAX, { decimals: 0 }));
  }
  if (!vide(s.ug_pct) && (Number(s.ug_pct) < 0 || Number(s.ug_pct) > 100)) erreurs.ug_pct = M_S1.UG_PCT_INVALIDE;
  const st = quantitesLigne(s);
  if (!erreurs.quantite && !erreurs.ug && st.erreurs.includes(ERREURS_QUANTITES_ACHAT.UG_SANS_UNITE_PAYEE)) {
    erreurs.ug = M_S1.UG_SANS_UNITE_PAYEE; // A-74 — « UG incluses »: at least one paid unit
  }

  if (p?.is_psychotrope && vide(String(s.numero_lot ?? "").trim())) erreurs.numero_lot = M_S1.LOT_PSYCHOTROPE;

  const mode = modePeremption(p?.type_produit, typeAchat);
  if (mode !== PEREMPTION_MODE.MASQUEE) {
    if (vide(s.date_peremption)) {
      if (mode === PEREMPTION_MODE.REQUISE) erreurs.date_peremption = M_S1.PEREMPTION_REQUISE;
    } else if (!estPeremptionSentinelle(s.date_peremption) && s.date_peremption <= aujourdhui) {
      erreurs.date_peremption = M_S1.PEREMPTION_PASSEE;
    }
  }

  const pu = dec(s.prix_achat_ht);
  const maxPu = formatDecimal(L.PRIX_ACHAT_HT_MAX, { decimals: 2 });
  if (pu == null || Number(pu) < 0 || Number(pu) > L.PRIX_ACHAT_HT_MAX) erreurs.prix_achat_ht = M_S1.PU_INVALIDE(maxPu);
  else if (Number(pu) === 0) avertissements.prix_achat_ht = M_S1.PU_ZERO;

  if (!erreurs.quantite && pu != null && !vide(s.ristourne)) {
    const brut = versCentimes(pu) * qte(s.quantite);
    if (versCentimes(s.ristourne) > brut) erreurs.ristourne = M_S1.RISTOURNE_TROP(fmtMoney(versTexteDecimal(brut, 2)));
  }

  if (!TAUX_TVA_ACHAT.includes(Number(s.taux_tva))) {
    erreurs.taux_tva = M_S1.TVA_INVALIDE(TAUX_TVA_ACHAT.map((t) => `${t} %`).join(", "));
  }

  // WP-69 — a non-médicament line says « prix de vente » (the same stored field and rule)
  const pv = margeApplicable(s);
  if (pv && (s.ordre_prix ?? []).includes(CHAMP_MARGE) && !vide(s.marge_pct)) {
    const m = Number(dec(s.marge_pct));
    if (!Number.isFinite(m) || m < MARGE_SAISIE.MIN || m > MARGE_SAISIE.MAX) {
      erreurs.marge_pct = M_S1.MARGE_INVALIDE(formatDecimal(MARGE_SAISIE.MIN, { decimals: 0 }), formatDecimal(MARGE_SAISIE.MAX, { decimals: 2 }));
    }
  }
  const ppa = dec(s.prix_vente_ppa);
  if (ppa == null || Number(ppa) < 0 || Number(ppa) > L.PRIX_VENTE_PPA_MAX) {
    erreurs.prix_vente_ppa = (pv ? M_S1.PV_INVALIDE : M_S1.PPA_INVALIDE)(formatDecimal(L.PRIX_VENTE_PPA_MAX, { decimals: 2 }));
  } else if (pu != null && versCentimes(ppa) < versCentimes(pu)) {
    erreurs.prix_vente_ppa = pv ? M_S1.PV_INFERIEUR : M_S1.PPA_INFERIEUR;
  }

  // D-25 / A-75 / A-99 — a typed Qté reçue (owner flag) stays within [UG, Qté reçue attendue]; the
  // difference with the derived one is said inline (the shortfall is attributed to the paid units).
  const attendue = formatDecimal(st.recue_attendue, { decimals: 0 });
  if (!s.recue_liee) {
    const r = dec(s.quantite_recue);
    if (s.recue_modifiable && (r == null || !/^\d+$/.test(r))) erreurs.quantite_recue = M_S1.RECUE_INVALIDE;
    else if (!erreurs.quantite && !erreurs.ug && st.erreurs.includes(ERREURS_QUANTITES_ACHAT.QUANTITE_RECUE_SUPERIEURE)) {
      erreurs.quantite_recue = M_S1.RECUE_TROP(attendue);
    } else if (!erreurs.quantite && !erreurs.ug && st.erreurs.includes(ERREURS_QUANTITES_ACHAT.QUANTITE_RECUE_INFERIEURE_AUX_UG)) {
      erreurs.quantite_recue = M_S1.RECUE_SOUS_UG(formatDecimal(st.ug, { decimals: 0 }));
    } else if (!erreurs.quantite && !erreurs.ug && st.manquant > 0) {
      avertissements.quantite_recue = M_S1.RECUE_ECART(formatDecimal(st.recue, { decimals: 0 }), attendue, st.manquant);
    }
  }

  const premier = CHAMPS_SAISIE.find((c) => erreurs[c]) ?? null;
  return { erreurs, avertissements, premier };
}

/** D-14 — true when `produit` would mix psychotropes and standard products on the facture (M-S1-30). */
export function psychotropeRefuse(produit, lignes, sauf = null) {
  const autres = (lignes ?? []).filter((l) => l.cle !== sauf && l.produit);
  if (autres.length === 0) return false;
  const psy = Boolean(produit?.is_psychotrope);
  return autres.some((l) => Boolean(l.produit.is_psychotrope) !== psy);
}

/** The facture holds psychotropes (the « Facture de psychotropes » tag, ux/02 §4.12). */
export const facturePsychotropes = (lignes) => (lignes ?? []).some((l) => l.produit?.is_psychotrope);

// ─── Engine views ───────────────────────────────────────────────────────────

/** The engine input of one line (calculerLigne): an entry being typed reads empty fields as 0. */
export function entreeMoteur(s) {
  // WP-63 — the engine computes the STORED quantities (paid units, UG, paid units received) of the
  // entry's mode; the UG it was given already follows the typed UG % (A-01, A-74).
  const st = quantitesLigne(s);
  return {
    quantite: st.quantite,
    ug: st.ug,
    maitre_ug: MAITRE_UG.UG,
    prix_achat_ht: dec(s.prix_achat_ht) ?? 0,
    ristourne: dec(s.ristourne) ?? 0,
    taux_tva: dec(s.taux_tva) ?? 0,
    prix_vente_ppa: dec(s.prix_vente_ppa) ?? 0,
    shp: dec(s.shp) ?? 0,
    quantite_recue: st.quantite_recue,
  };
}

/**
 * The preview line of the band (ux/02 §4.7): Line HT (and the struck brut when a ristourne is
 * typed), stock entry, shortfall, A-13 marge %, and the CHIFA théorique of a paid unit for a
 * remboursable product with a T.R (D-05 / A-10, rates from the pharmacy settings).
 */
export function apercuSaisie(s, parametres) {
  let calc;
  try {
    calc = calculerLigne(entreeMoteur(s));
  } catch {
    return null;
  }
  const p = s.produit;
  let chifa = null;
  if (p?.is_remboursable && !vide(p.tarif_ref) && Number(p.tarif_ref) > 0 && parametres) {
    const cle = p.is_local ? "taux_incentive_chifa_local_pct" : "taux_incentive_chifa_importe_pct";
    if (!vide(parametres[cle])) {
      chifa = versTexteDecimal(
        margeUnitaire({
          canal: CANAL_VENTE.CHIFA,
          bucket: BUCKET_LOT.PAYEE,
          prix_vente_ppa: dec(s.prix_vente_ppa) ?? 0,
          prix_achat_ht: dec(s.prix_achat_ht) ?? 0,
          tarif_ref: p.tarif_ref,
          taux_incentive: parametres[cle],
        }),
        2,
      );
    }
  }
  return {
    montant_ht: versTexteDecimal(calc.montant_ht_centimes, 2),
    brut: versTexteDecimal(calc.montant_brut_centimes, 2),
    ristourne: versTexteDecimal(calc.ristourne_centimes, 2),
    avec_ristourne: calc.ristourne_centimes > 0,
    ug: calc.ug,
    recue: calc.quantite_recue,
    entree_stock: calc.entree_stock,
    manquant: calc.manquant,
    marge_pct: calc.marge_pct,
    // WP-69 — the line's margin is an input of the band (the preview no longer repeats it)
    marge_saisissable: margeApplicable(s),
    chifa_theorique: chifa,
    anomalies: calc.anomalies,
  };
}

/** The six declared totals of an empty header (« Remplir totaux », D-09). */
export const declaresVides = () =>
  Object.fromEntries(TOTAUX_DECLARES.map((t) => [t.declare, null]));

/** A new facture header (ux/02 §4.5 defaults: dates today, Type d'achat Médicament). */
export function nouvelEntete(aujourdhui = todayIso()) {
  return {
    fournisseur_id: "",
    fournisseur: null,
    numero_facture_fournisseur: "",
    date_facture: aujourdhui,
    date_livraison: aujourdhui,
    magasin_id: "",
    type_achat: TYPE_ACHAT_DEFAUT,
    // WP-68 (A-109 (1)) — the owner type the facture is entered as; null = the predefined type of type_achat
    type_achat_officine_id: null,
    notes: "",
    maitre_remise: MAITRE_REMISE.MONTANT,
    remise_globale_montant: null,
    remise_globale_pct: null,
    declares: declaresVides(),
    // Refonte WP-63 (D-25) — the QTE reçue mode the lines are typed in: the settings' mode until the
    // draft exists, then the facture's snapshot (never sent: the server sets it at creation).
    mode_qte_recue: MODE_QTE_RECUE.PAYEE_PLUS_UG,
  };
}

/** The engine header (remise globale + declared totals). */
export function enteteMoteur(e) {
  return {
    maitre_remise: e.maitre_remise,
    remise_globale_montant: e.maitre_remise === MAITRE_REMISE.MONTANT ? dec(e.remise_globale_montant) ?? 0 : null,
    remise_pct: e.maitre_remise === MAITRE_REMISE.POURCENT ? dec(e.remise_globale_pct) ?? 0 : null,
    ...Object.fromEntries(Object.entries(e.declares ?? {}).map(([k, v]) => [k, dec(v)])),
  };
}

/**
 * Invoice totals of the facture (ux/02 §2.2-§2.3) through the shared engine, with the pharmacy
 * tolerance (A-06). Returns null while a line cannot be read (never a half figure).
 */
export function totauxFacture(lignes, entete, parametres) {
  if (!parametres) return null;
  try {
    return calculerTotaux((lignes ?? []).map(entreeMoteur), enteteMoteur(entete), parametres);
  } catch {
    return null;
  }
}

export { ETAT_ECART_TTC };

/** How many of the six declared totals are typed (« 3 / 6 totaux saisis », ux/02 §4.5 #8). */
export function nombreTotauxSaisis(declares) {
  return TOTAUX_DECLARES.filter((t) => !vide(declares?.[t.declare])).length;
}

/** The units a line enters into stock (the engine's `entree_stock`; 0 for an unreadable line). */
export function unitesEntrantes(l) {
  try {
    return calculerLigne(entreeMoteur(l)).entree_stock;
  } catch {
    return 0;
  }
}

/**
 * Lines footer (ux/02 §4.8) and D5 summary (§4.16): count, units entering stock, shortfall in
 * units and the number of lines short of paid units (« 1 unité manquante sur 1 ligne »).
 */
export function resumeLignes(lignes) {
  let stock = 0;
  let manquant = 0;
  let lignesManquantes = 0;
  for (const l of lignes ?? []) {
    try {
      const c = calculerLigne(entreeMoteur(l));
      stock += c.entree_stock;
      manquant += c.manquant;
      if (c.manquant > 0) lignesManquantes += 1;
    } catch {
      /* an unreadable line counts nothing */
    }
  }
  return { n: (lignes ?? []).length, stock, manquant, lignes_manquantes: lignesManquantes };
}

/**
 * Lines whose product type differs from the Type d'achat (the type tag of ux/02 §4.8) — by type KEY
 * (WP-68): a « Lait » product on a « Lait » facture is the same type, a Parapharmacie one is not.
 * @param {object} ligne
 * @param {string} cleAchat  `cleTypeAchat(entete)`
 */
export function typeDifferent(ligne, cleAchat) {
  const t = cleTypeProduit(ligne?.produit);
  return Boolean(t) && Boolean(cleAchat) && cleAchat !== TYPE_ACHAT_MIXTE && t !== cleAchat;
}

// ─── Header checks (ux/02 §4.5) ─────────────────────────────────────────────

/** M-S1-03 / M-S1-04 / M-S1-07 / the N° length — errors and warnings of the B1 fields. */
export function verifierEntete(e, { aujourdhui = todayIso() } = {}) {
  const erreurs = {};
  const avertissements = {};
  if (vide(e.date_facture)) erreurs.date_facture = M_S1.DATE_FUTURE;
  else if (e.date_facture > aujourdhui) erreurs.date_facture = M_S1.DATE_FUTURE;
  if (!vide(e.date_livraison) && !vide(e.date_facture) && e.date_livraison < e.date_facture) {
    avertissements.date_livraison = M_S1.RECEPTION_AVANT_FACTURE;
  }
  if (String(e.notes ?? "").length > L.NOTES_MAX) erreurs.notes = M_S1.NOTES_LONGUEUR(formatDecimal(L.NOTES_MAX, { decimals: 0 }));
  if (String(e.numero_facture_fournisseur ?? "").trim().length > L.NUMERO_MAX) {
    erreurs.numero_facture_fournisseur = M_S1.NOTES_LONGUEUR(formatDecimal(L.NUMERO_MAX, { decimals: 0 }));
  }
  return { erreurs, avertissements };
}

// ─── Server payloads ────────────────────────────────────────────────────────

const num = (v) => (vide(v) ? null : Number(dec(v)));

/**
 * One line → a `details[]` entry of POST / PATCH /achats (the D-01…D-08 vocabulary, A-45 legacy
 * fields never sent). WP-63 — the STORED quantities in both modes (A-74): paid units, UG, paid units
 * received; a UG % line also carries the UG it gave, which the server keeps in « UG incluses »
 * (the % typed on a Qté that holds the UG).
 */
export function versDetailServeur(l, typeAchat) {
  const st = quantitesLigne(l);
  return {
    ...(l.id ? { id: l.id } : {}),
    produit_id: l.produit?.id,
    quantite: st.quantite,
    ...(l.maitre_ug === MAITRE_UG.UG_PCT
      ? { ug: st.ug, ug_pct: num(l.ug_pct) ?? 0, maitre_ug: MAITRE_UG.UG_PCT }
      : { ug: st.ug, maitre_ug: MAITRE_UG.UG }),
    quantite_recue: st.quantite_recue,
    prix_achat_ht: num(l.prix_achat_ht) ?? 0,
    ristourne: num(l.ristourne) ?? 0,
    taux_tva: Number(l.taux_tva ?? 0),
    prix_vente_ppa: num(l.prix_vente_ppa) ?? 0,
    shp: Number(dec(l.shp) ?? 0),
    numero_lot: String(l.numero_lot ?? "").trim() || null,
    date_peremption: peremptionEnvoyee(l, typeAchat),
    // WP-66 (D-29) — the pair, always sent by S1 / S3 (null = the facture's magasin)
    ...destinationVersServeur(l),
    // WP-32 contract — sent = the line's links replaced, absent = kept (a line never linked sends none)
    ...(Array.isArray(l.bons_commande) && l.bons_commande.length
      ? { bons_commande: l.bons_commande.map((b) => ({ ligne_bon_commande_id: b.ligne_bon_commande_id })) }
      : {}),
  };
}

/** The B1 header + lines → the body of POST / PATCH /achats (N-01 replace semantics on PATCH). */
export function versCorpsServeur(entete, lignes) {
  const corps = {
    fournisseur_id: entete.fournisseur_id || null,
    numero_facture_fournisseur: String(entete.numero_facture_fournisseur ?? "").trim() || null,
    date_facture: entete.date_facture,
    date_livraison: entete.date_livraison || null,
    magasin_id: entete.magasin_id || null,
    type_achat: entete.type_achat,
    // WP-68 — the owner type (null = the predefined type of type_achat); a kept one is left as stored
    type_achat_officine_id: entete.type_achat_officine_id ?? null,
    notes: String(entete.notes ?? "").trim() || null,
    maitre_remise: entete.maitre_remise,
    remise_globale_montant: entete.maitre_remise === MAITRE_REMISE.MONTANT ? num(entete.remise_globale_montant) ?? 0 : null,
    remise_globale_pct: entete.maitre_remise === MAITRE_REMISE.POURCENT ? num(entete.remise_globale_pct) ?? 0 : null,
    details: (lignes ?? []).map((l) => versDetailServeur(l, entete.type_achat)),
  };
  for (const t of TOTAUX_DECLARES) corps[t.declare] = num(entete.declares?.[t.declare]);
  return corps;
}

/**
 * A line of `GET /achats/:id` → an entry (ux/02 §4.1: hydrated from `detail.produit`), typed in the
 * facture's rule `regle` = `{ mode, modifiable }` (WP-63: « Qté » holds the UG in « UG incluses »,
 * « Qté reçue » = the units that entered / enter the stock).
 */
export function depuisDetailServeur(d, regle = {}) {
  const maitre = d.maitre_ug === MAITRE_UG.UG_PCT ? MAITRE_UG.UG_PCT : MAITRE_UG.UG;
  const date = d.date_peremption ? String(d.date_peremption).slice(0, 10) : "";
  const vue = quantitesSaisies(modeQteRecue(regle.mode), { quantite: d.quantite ?? 1, ug: d.ug ?? 0, quantite_recue: d.quantite_recue });
  return {
    ...nouvelleSaisie(regle),
    id: d.id,
    produit: normaliserProduit(d.produit ? { ...d.produit, id: d.produit.id ?? d.produit_id } : null),
    quantite: String(vue.quantite),
    ug: String(vue.ug),
    ug_pct: dec(d.ug_pct),
    maitre_ug: maitre,
    numero_lot: d.numero_lot ?? "",
    date_peremption: estPeremptionSentinelle(date) ? "" : date,
    prix_achat_ht: dec(d.prix_achat_ht),
    ristourne: dec(d.ristourne),
    taux_tva: String(Number(d.taux_tva ?? 0)),
    prix_vente_ppa: dec(d.prix_vente_ppa),
    shp: dec(d.shp) ?? "0",
    quantite_recue: String(vue.quantite_recue),
    recue_liee: (d.quantite_recue ?? d.quantite) === d.quantite,
    destination: destinationDepuisServeur(d), // WP-66
    bons_commande: Array.isArray(d.bons_commande_lignes) && d.bons_commande_lignes.length
      ? d.bons_commande_lignes.map((b) => ({
          ligne_bon_commande_id: b.ligne_bon_commande_id,
          bon_commande_id: b.ligne_bon_commande?.bon_commande?.id ?? null,
          numero: b.ligne_bon_commande?.bon_commande?.numero ?? null,
        }))
      : null,
  };
}

/**
 * `GET /achats/:id` → { entete, lignes } for /achats/:id/modifier and S3 edit mode: the lines typed
 * in the facture's snapshot mode (`a.mode_qte_recue`, D-25) and the owner flag of `opts`.
 */
export function depuisAchatServeur(a, { recue_modifiable = false } = {}) {
  const regle = { mode: modeQteRecue(a.mode_qte_recue), modifiable: recue_modifiable };
  const remisePct = dec(a.remise_pct);
  const pourcent = remisePct != null && Number(remisePct) > 0;
  const declares = declaresVides();
  for (const t of TOTAUX_DECLARES) declares[t.declare] = dec(a[t.declare]);
  return {
    entete: {
      fournisseur_id: a.fournisseur_id ?? "",
      fournisseur: a.fournisseur ? { id: a.fournisseur.id, nom: a.fournisseur.nom } : null,
      numero_facture_fournisseur: a.numero_facture_fournisseur ?? "",
      date_facture: a.date_facture ? String(a.date_facture).slice(0, 10) : todayIso(),
      date_livraison: a.date_livraison ? String(a.date_livraison).slice(0, 10) : "",
      magasin_id: a.magasin_id ?? "",
      type_achat: a.type_achat ?? TYPE_ACHAT_DEFAUT,
      type_achat_officine_id: a.type_achat_officine_id ?? null, // WP-68
      type_achat_officine: a.type_achat_officine ?? null,
      notes: a.notes ?? "",
      maitre_remise: pourcent ? MAITRE_REMISE.POURCENT : MAITRE_REMISE.MONTANT,
      remise_globale_montant: pourcent ? null : dec(a.remise_globale_montant),
      remise_globale_pct: pourcent ? remisePct : null,
      declares,
      mode_qte_recue: regle.mode,
    },
    lignes: (a.details ?? []).map((d) => depuisDetailServeur(d, regle)),
  };
}

// ─── Prefilled entries (the completion loop, ux/02 §4.7 / §4.14) ───────────

/** A product known only by id and name (Secours, Manquants, OCR): completed by `completerProduit`. */
const produitMinimal = (id, nom, extra = {}) =>
  id ? { ...normaliserProduit({ id, nom_commercial: nom ?? "", ...extra }), incomplet: true } : null;

/**
 * A prefilled entry: what the source knows, the rest left to the operator (prefill-and-confirm).
 * The source quantities are STORED ones (paid units, UG — a bon de commande, an e-facture, OCR):
 * the entry is built in « Qté payée + UG » and the S1 / S3 reducer re-types it in the facture's
 * mode (`avecRegleRecue`, WP-63), Qté reçue derived.
 */
export function saisiePrefill(src) {
  const base = nouvelleSaisie();
  const quantite = entier(src.quantite) ?? "1";
  const ug = entier(src.ug) ?? "0";
  return {
    ...base,
    produit: src.produit ?? null,
    quantite,
    quantite_recue: String(qte(quantite) + qte(ug)),
    ug,
    numero_lot: src.numero_lot ?? "",
    date_peremption: src.date_peremption ? String(src.date_peremption).slice(0, 10) : "",
    prix_achat_ht: dec(src.prix_achat_ht),
    ristourne: dec(src.ristourne),
    taux_tva: src.taux_tva != null && src.taux_tva !== "" ? String(Number(src.taux_tva)) : null,
    prix_vente_ppa: dec(src.prix_vente_ppa),
    shp: src.shp != null && src.shp !== "" ? dec(src.shp) : null,
    origine: src.origine,
    a_verifier: true,
    texte_recherche: src.texte_recherche ?? null,
    verrouillee: Boolean(src.verrouillee),
    ef_detail_id: src.ef_detail_id ?? null,
    bons_commande: Array.isArray(src.bons_commande) && src.bons_commande.length ? src.bons_commande : null,
  };
}

/** The full product loaded for a prefilled entry: TVA / PPA / SHP of the product only where the source had none. */
export function completerProduit(s, produit) {
  const p = normaliserProduit(produit);
  if (!p) return s;
  return {
    ...s,
    produit: p,
    taux_tva: s.taux_tva ?? (p.taux_tva != null ? String(Number(p.taux_tva)) : "0"),
    prix_vente_ppa: s.prix_vente_ppa ?? p.ppa,
    shp: s.shp ?? shpDuProduit(p),
  };
}

/** Secours rows (SecoursPicker): product + N° lot prefilled, the quantity due proposed (NouvelAchat.jsx:2019-2059). */
export const saisiesSecours = (choisis) =>
  (choisis ?? [])
    .filter((g) => g.produit_id)
    .map((g) =>
      saisiePrefill({
        produit: produitMinimal(g.produit_id, g.nom, { dosage: g.dosage, type_produit: g.type_produit }),
        quantite: g.quantite || 1,
        numero_lot: g.numero_lot ?? "",
        date_peremption: g.date_peremption_declaree ?? null,
        origine: "SECOURS",
      }),
    );

/** Manquants rows (ManquantsPicker): product and the missing quantity (NouvelAchat.jsx:1980-2004). */
export const saisiesManquants = (choisis) =>
  (choisis ?? [])
    .filter((m) => m.produit_id)
    .map((m) =>
      saisiePrefill({
        produit: produitMinimal(m.produit_id, m.nom, { dosage: m.dosage, type_produit: m.type_produit }),
        quantite: m.quantite || 1,
        origine: "MANQUANT",
      }),
    );

/** OCR facture lines (`POST /ocr/extract-facture`): a line without a catalogue product keeps its designation as the search text. */
export const saisiesOcrFacture = (details) =>
  (details ?? []).map((d) =>
    saisiePrefill({
      produit: produitMinimal(d.produit_id, d.produit_nom, { is_psychotrope: d.is_psychotrope, num_enr_cnas: d.num_enr_cnas }),
      quantite: d.quantite ?? 1,
      prix_achat_ht: d.prix_achat_ht,
      prix_vente_ppa: d.prix_vente_ppa,
      shp: d.shp,
      numero_lot: d.numero_lot ?? "",
      date_peremption: d.date_peremption,
      origine: "OCR",
      texte_recherche: d.produit_id ? null : d.produit_nom || d.nom || null,
    }),
  );

/** The OCR vignette result (VignetteScanPanel, mode « achat »): one entry. */
export const saisieVignette = (r) =>
  saisiePrefill({
    produit: r?.produit ? { ...normaliserProduit(r.produit), incomplet: false } : null,
    quantite: r?.quantite || 1,
    prix_vente_ppa: r?.ppa ?? r?.produit?.ppa ?? null,
    numero_lot: r?.numero_lot ?? "",
    date_peremption: r?.date_peremption,
    taux_tva: r?.produit?.taux_tva,
    origine: "OCR",
  });

/**
 * An e-facture (import by fournisseur, QR or code): lines with a catalogue product are LOCKED lines
 * of the facture (driven by the supplier document, NouvelAchat.jsx:2830-2874); a line without one
 * goes to the completion loop.
 */
export function depuisEFacture(ef) {
  const details = ef?.details ?? ef?.lignes ?? [];
  const lignes = [];
  const aCompleter = [];
  for (const d of details) {
    const s = saisiePrefill({
      produit: produitMinimal(d.produit_id, d.produit_nom || d.nom, { dosage: d.dosage }),
      quantite: d.quantite_commandee || d.quantite || 1,
      ug: d.ug ?? 0,
      prix_achat_ht: d.prix_achat_ht,
      taux_tva: d.tva ?? d.taux_tva ?? 0,
      prix_vente_ppa: d.prix_vente_ppa,
      shp: d.shp ?? 0,
      numero_lot: d.numero_lot ?? "",
      date_peremption: d.date_peremption,
      origine: "EFACTURE",
      verrouillee: Boolean(d.produit_id),
      ef_detail_id: d.id ?? null,
      texte_recherche: d.produit_id ? null : d.produit_nom || d.nom || null,
    });
    if (s.verrouillee) lignes.push({ ...s, origine: "MANUELLE", a_verifier: false });
    else aCompleter.push(s);
  }
  return {
    lignes,
    aCompleter,
    entete: {
      numero_facture_fournisseur: ef?.numero || ef?.num || "",
      fournisseur_id: ef?.fournisseur_id || "",
      ...(ef?.date_creation ? { date_facture: String(ef.date_creation).slice(0, 10) } : {}),
    },
    efacture_id: ef?.id ?? null,
  };
}
